import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { defaultTheme, type RawTheme } from './defaultTheme';

/** Matches `.lola-layout { max-width: 500px }` so exports aren't squeezed. */
export const CAPTURE_WIDTH = 500;
/** Tall enough for the card + outer aura padding without looking phone-stretched. */
export const CAPTURE_HEIGHT = 860;

interface GalleryState {
  /** Current raw theme applied to the widget (font + colors). */
  rawTheme: RawTheme;
  /** Apply a new raw theme (already validated). */
  setRawTheme: (theme: RawTheme) => void;
  /** Restore the bundled default theme. */
  resetTheme: () => void;
  /** Keep the control dock open after navigating between screens. */
  pinned: boolean;
  /** Toggle / set the pinned state. */
  setPinned: (pinned: boolean) => void;
  /** Ordered screen ids for the export sequence. */
  sequence: string[];
  /** Thumbnail data URLs keyed by screen id (not persisted). */
  thumbs: Record<string, string>;
  /** True while the PNG export loop is running. */
  exporting: boolean;
  /**
   * Saved lists in the galleries: cash-pickup habits (send/request/claim) and
   * recent recipients (send/deferred).
   */
  savedListsUi: "populated" | "loading" | "empty";
  addToSequence: (id: string) => void;
  removeFromSequence: (id: string) => void;
  reorderSequence: (ids: string[]) => void;
  clearSequence: () => void;
  setThumb: (id: string, dataUrl: string) => void;
  setExporting: (exporting: boolean) => void;
  setSavedListsUi: (savedListsUi: "populated" | "loading" | "empty") => void;
}

export const useGalleryStore = create<GalleryState>()(
  persist(
    (set, get) => ({
      rawTheme: defaultTheme,
      setRawTheme: (rawTheme) => set({ rawTheme }),
      resetTheme: () => set({ rawTheme: defaultTheme }),
      pinned: false,
      setPinned: (pinned) => set({ pinned }),
      sequence: [],
      thumbs: {},
      exporting: false,
      savedListsUi: "populated",
      addToSequence: (id) => {
        const { sequence } = get();
        if (sequence.includes(id)) return;
        set({ sequence: [...sequence, id] });
      },
      removeFromSequence: (id) => {
        const { sequence, thumbs } = get();
        const nextThumbs = { ...thumbs };
        delete nextThumbs[id];
        set({
          sequence: sequence.filter((entry) => entry !== id),
          thumbs: nextThumbs,
        });
      },
      reorderSequence: (ids) => set({ sequence: ids }),
      clearSequence: () => set({ sequence: [], thumbs: {} }),
      setThumb: (id, dataUrl) =>
        set((state) => ({ thumbs: { ...state.thumbs, [id]: dataUrl } })),
      setExporting: (exporting) => set({ exporting }),
      setSavedListsUi: (savedListsUi) => set({ savedListsUi }),
    }),
    {
      name: 'raas-widgets-screenbook-theme',
      partialize: (state) => ({
        rawTheme: state.rawTheme,
        pinned: state.pinned,
        sequence: state.sequence,
      }),
    },
  ),
);
